import { visibleTabs } from '@amzn/journeys-common';
import { Column, Loader, Text } from '@amzn/meridian';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { ImageSelectorModalContainer } from 'features/assets/ImageSelectorModalContainer';
import { useMemo } from 'react';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';
import { EmptyStateView } from './components/EmptyStateView';
import { ImagePreviewTable } from './components/ImagePreviewTable';
import { useAssetManagement } from './hooks/useAssetManagement';

export const FileType = {
  Caption: 'caption',
  Image: 'image',
  Video: 'video',
} as const;

export const MediaPicker = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const { label, onChange, value, schema, uiSchema, required } = props;

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);

  const { requiredWidth, requiredHeight } = useMemo(() => {
    const uiOptions = uiSchema?.['ui:options'] as Record<string, unknown> | undefined;
    return {
      requiredHeight: uiOptions?.requiredHeight as number | undefined,
      requiredWidth: uiOptions?.requiredWidth as number | undefined,
    };
  }, [
    uiSchema,
  ]);

  const getValidFileTypes = () => {
    const description = schema.description;

    switch (description) {
      case FileType.Video:
        return [
          'video/mp4',
          'video/webm',
        ];
      case FileType.Caption:
        return [
          'text/vtt',
        ];
      default:
        return [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/gif',
        ];
    }
  };

  const {
    currentAsset,
    openModal,
    loading,
    trafficProjectId,
    handleOpenModal,
    handleCloseModal,
    handleRemoveImage,
    handleOnAssetsReady,
    validationError,
  } = useAssetManagement({
    onChange: requiredFieldOnChange,
    requiredHeight,
    requiredWidth,
    value,
  });
  return (
    <Column alignmentHorizontal="start" spacing="100">
      {label && (
        <Text color="primary" type="b300">
          {label}
        </Text>
      )}
      {loading && <Loader />}
      {!loading && !currentAsset && <EmptyStateView onUploadClick={handleOpenModal} />}
      {currentAsset && <ImagePreviewTable asset={currentAsset} onRemoveClick={handleRemoveImage} onUploadClick={handleOpenModal} />}
      <ImageSelectorModalContainer
        filterParams={[]}
        multiselect={false}
        onAssetsReady={handleOnAssetsReady}
        onHide={handleCloseModal}
        projectId={trafficProjectId}
        validFileTypes={getValidFileTypes()}
        visible={openModal}
        visibleTabs={visibleTabs}
      />
      {validationError && (
        <Text color="error" type="b200">
          {validationError}
        </Text>
      )}
    </Column>
  );
};
